import { all, call, put, takeLatest } from "redux-saga/effects";
import Swal from "sweetalert2";

import {
    FETCH_OFFRE_SALARIALE_BY_CANDIDATURE_REQUEST,
    fetchOffreSalarialeByCandidatureSuccess,
    fetchOffreSalarialeByCandidatureFail,

    CREATE_OFFRE_SALARIALE_REQUEST,
    createOffreSalarialeSuccess,
    createOffreSalarialeFail,

    UPDATE_OFFRE_SALARIALE_REQUEST,
    updateOffreSalarialeSuccess,
    updateOffreSalarialeFail,

    DELETE_OFFRE_SALARIALE_REQUEST,
    deleteOffreSalarialeSuccess,
    deleteOffreSalarialeFail,

    DOWNLOAD_OFFRE_SALARIALE_DOCUMENT_REQUEST,
    downloadOffreSalarialeDocumentSuccess,
    downloadOffreSalarialeDocumentFail,

    SEND_OFFRE_SALARIALE_EMAIL_REQUEST,
    sendOffreSalarialeEmailSuccess,
    sendOffreSalarialeEmailFail,

    setOffresSalarialesLoading,
} from "../actions/offresSalarialesActions";

import {
    API_BASE_URL,
    deleteRequest,
    getRequest,
    getRequestForPdf,
    postRequest,
    putRequest,
} from "../../../api";

const getUid = () => sessionStorage.getItem("uid");
const getToken = () => sessionStorage.getItem("token");

const mapModalPayloadToBackend = (payload) => ({
    candidatureId: payload.candidatureId,
    typeSalaire: String(payload.typeSalaire || "BRUT").toUpperCase(),
    salaire: payload.salaireBase !== "" && payload.salaireBase !== null ? Number(payload.salaireBase) : null,
    primeBonus: payload.primes !== "" && payload.primes !== null ? Number(payload.primes) : null,
    avantages: payload.avantages || "",
    datePrisePoste: payload.dateDebut || null,
    periodeEssai: payload.periodeEssai || "",
    commentaire: payload.noteComplementaire || "",
    statutOffre: payload.statutOffre || null,
});

const downloadBlob = (data, filename) => {
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

const extractFilename = (contentDisposition, fallback) => {
    if (!contentDisposition) return fallback;

    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
        return decodeURIComponent(utf8Match[1]);
    }

    const basicMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
    return basicMatch?.[1] || fallback;
};

function* triggerSendEmail(offreId, uid, token) {
    const sendUrl = `${API_BASE_URL}/api/offres-salariales/${offreId}/send-email`;
    const sendResp = yield call(postRequest, {
        url: sendUrl,
        data: {},
        uid,
        token,
    });
    yield put(sendOffreSalarialeEmailSuccess(sendResp.data));
}

export function* fetchOffreSalarialeByCandidatureSaga(action) {
    const { candidatureId } = action.payload;
    const url = `${API_BASE_URL}/api/offres-salariales/by-candidature/${candidatureId}`;
    const uid = getUid();
    const token = getToken();

    yield put(setOffresSalarialesLoading(true));
    try {
        const resp = yield call(getRequest, { url, uid, token });
        yield put(fetchOffreSalarialeByCandidatureSuccess(resp.data));
    } catch (e) {
        const status = e?.response?.status;
        if (status === 404) {
            yield put(fetchOffreSalarialeByCandidatureFail(null));
        } else {
            yield put(fetchOffreSalarialeByCandidatureFail(e.response?.data?.message || e.message));
        }
    } finally {
        yield put(setOffresSalarialesLoading(false));
    }
}

export function* createOffreSalarialeSaga(action) {
    const url = `${API_BASE_URL}/api/offres-salariales`;
    const uid = getUid();
    const token = getToken();

    yield put(setOffresSalarialesLoading(true));
    try {
        const body = mapModalPayloadToBackend(action.payload);
        const resp = yield call(postRequest, {
            url,
            data: body,
            uid,
            token,
        });

        yield put(createOffreSalarialeSuccess(resp.data));

        if (action.payload?.envoyerEmail && resp.data?.id) {
            yield call(triggerSendEmail, resp.data.id, uid, token);
        }

        yield call([Swal, Swal.fire], {
            icon: "success",
            text: action.payload?.envoyerEmail
                ? "Offre salariale enregistree et envoyee par email"
                : "Offre salariale enregistree",
            timer: 1500,
            showConfirmButton: false,
        });
    } catch (e) {
        const msg = e.response?.data?.message || "Erreur creation offre salariale";
        yield put(createOffreSalarialeFail(msg));
        yield call([Swal, Swal.fire], {
            icon: "error",
            text: msg,
            timer: 2200,
            showConfirmButton: false,
        });
    } finally {
        yield put(setOffresSalarialesLoading(false));
    }
}

export function* updateOffreSalarialeSaga(action) {
    const { id, payload } = action.payload;
    const url = `${API_BASE_URL}/api/offres-salariales/${id}`;
    const uid = getUid();
    const token = getToken();

    yield put(setOffresSalarialesLoading(true));
    try {
        const body = mapModalPayloadToBackend(payload);
        const resp = yield call(putRequest, {
            url,
            data: body,
            uid,
            token,
        });

        yield put(updateOffreSalarialeSuccess(resp.data));

        if (payload?.envoyerEmail && resp.data?.id) {
            yield call(triggerSendEmail, resp.data.id, uid, token);
        }

        yield call([Swal, Swal.fire], {
            icon: "success",
            text: payload?.envoyerEmail
                ? "Offre salariale modifiee et envoyee par email"
                : "Offre salariale modifiee",
            timer: 1500,
            showConfirmButton: false,
        });
    } catch (e) {
        const msg = e.response?.data?.message || "Erreur modification offre salariale";
        yield put(updateOffreSalarialeFail(msg));
        yield call([Swal, Swal.fire], {
            icon: "error",
            text: msg,
            timer: 2200,
            showConfirmButton: false,
        });
    } finally {
        yield put(setOffresSalarialesLoading(false));
    }
}

export function* deleteOffreSalarialeSaga(action) {
    const { id, candidatureId } = action.payload;
    const url = `${API_BASE_URL}/api/offres-salariales/${id}`;
    const uid = getUid();
    const token = getToken();

    yield put(setOffresSalarialesLoading(true));
    try {
        yield call(deleteRequest, { url, uid, token });
        yield put(deleteOffreSalarialeSuccess({ id, candidatureId }));

        yield call([Swal, Swal.fire], {
            icon: "success",
            text: "Offre salariale supprimee",
            timer: 1500,
            showConfirmButton: false,
        });
    } catch (e) {
        const msg = e.response?.data?.message || "Erreur suppression offre salariale";
        yield put(deleteOffreSalarialeFail(msg));
        yield call([Swal, Swal.fire], {
            icon: "error",
            text: msg,
            timer: 2200,
            showConfirmButton: false,
        });
    } finally {
        yield put(setOffresSalarialesLoading(false));
    }
}

export function* downloadOffreSalarialeDocumentSaga(action) {
    const { id } = action.payload;
    const token = getToken();
    const uid = getUid();
    const url = `${API_BASE_URL}/api/offres-salariales/${id}/document/download`;

    yield put(setOffresSalarialesLoading(true));
    try {
        const resp = yield call(getRequestForPdf, {
            url,
            token,
            headers: { uid },
            responseType: "arraybuffer",
        });

        const filename = extractFilename(
            resp.headers?.["content-disposition"],
            `Offre_Salariale_${id}.pdf`
        );

        downloadBlob(resp.data, filename);
        yield put(downloadOffreSalarialeDocumentSuccess());

        yield call([Swal, Swal.fire], {
            icon: "success",
            text: "Le document d'offre salariale a ete telecharge",
            timer: 1800,
            showConfirmButton: false,
        });
    } catch (e) {
        const msg = e.response?.data?.message || "Erreur generation document offre salariale";
        yield put(downloadOffreSalarialeDocumentFail(msg));
        yield call([Swal, Swal.fire], {
            icon: "error",
            text: msg,
            timer: 2200,
            showConfirmButton: false,
        });
    } finally {
        yield put(setOffresSalarialesLoading(false));
    }
}

export function* sendOffreSalarialeEmailSaga(action) {
    const { id } = action.payload;
    const uid = getUid();
    const token = getToken();

    yield put(setOffresSalarialesLoading(true));
    try {
        yield call(triggerSendEmail, id, uid, token);
        yield call([Swal, Swal.fire], {
            icon: "success",
            text: "L'offre salariale a ete envoyee par email",
            timer: 1800,
            showConfirmButton: false,
        });
    } catch (e) {
        const msg = e.response?.data?.message || "Erreur envoi email offre salariale";
        yield put(sendOffreSalarialeEmailFail(msg));
        yield call([Swal, Swal.fire], {
            icon: "error",
            text: msg,
            timer: 2200,
            showConfirmButton: false,
        });
    } finally {
        yield put(setOffresSalarialesLoading(false));
    }
}

export default function* offresSalarialesSaga() {
    yield all([
        takeLatest(FETCH_OFFRE_SALARIALE_BY_CANDIDATURE_REQUEST, fetchOffreSalarialeByCandidatureSaga),
        takeLatest(CREATE_OFFRE_SALARIALE_REQUEST, createOffreSalarialeSaga),
        takeLatest(UPDATE_OFFRE_SALARIALE_REQUEST, updateOffreSalarialeSaga),
        takeLatest(DELETE_OFFRE_SALARIALE_REQUEST, deleteOffreSalarialeSaga),
        takeLatest(DOWNLOAD_OFFRE_SALARIALE_DOCUMENT_REQUEST, downloadOffreSalarialeDocumentSaga),
        takeLatest(SEND_OFFRE_SALARIALE_EMAIL_REQUEST, sendOffreSalarialeEmailSaga),
    ]);
}
